import Axios, {
    AxiosHeaders,
    AxiosRequestConfig,
    AxiosRequestHeaders,
    AxiosResponse,
    RawAxiosRequestConfig,
} from 'axios'
import { CancelledError } from 'react-query'
import { BASE_URL } from './config'
import posthog from 'posthog-js'
import { navigateTo } from 'utilities/navigation'

export const api = Axios.create({
    baseURL: BASE_URL,
    headers: {
        'Accept-Language': 'fa-IR',
        'CLIENT-TYPE': 'pwa',
    },
})

api.interceptors.request.use(
    async (config) => {
        const userProfile = window.localStorage.getItem('user')
        userProfile &&
            userProfile !== 'null' &&
            config.headers &&
            (config.headers as AxiosHeaders).set(
                'Authorization',
                `JWT ${JSON.parse(userProfile)?.token} `
            )
        return config
    },
    (error) => Promise.reject(error)
)

api.interceptors.response.use(
    (response) => {
        if (response.status === 200 || response.status === 201) {
            // toast.success(response?.data?.message)
        }
        return response
    },
    async function (error) {
        // toast.error(error?.response?.data?.message)

        if (error?.response?.status === 401) {
            posthog.capture('session_expired', {
                endpoint: error?.response?.config?.url,
            })
            window.localStorage.clear()
            posthog.reset()
            navigateTo('/auth/login')

            /**
             * Reject — and reject with react-query's own CancelledError.
             *
             * This used to `return navigateTo(...)`. Returning a value from a
             * response-error interceptor RESOLVES the chain with that value,
             * and `navigateTo` returns undefined, so every one of the ~160
             * `const { data } = await request.get(...)` fetchers in
             * `src/services/` destructured undefined and threw
             * `Cannot destructure property 'data' of ... as it is undefined`.
             * Two 401s land on a logged-out page load, so every logged-out
             * session opened with two red console errors.
             *
             * Plain `Promise.reject(error)` fixes the TypeError but not the
             * noise: react-query v3 `console.error`s every query error
             * unconditionally (`core/query.js:369`) and we set no custom
             * logger, so the 401 would just take the TypeError's place in the
             * console and in session recordings.
             *
             * A silent CancelledError is react-query's own way to say "this
             * request was abandoned, it did not fail": it skips both the error
             * dispatch and that log (`core/query.js:358-369`), so the query
             * simply stays pending while the browser leaves for the login
             * page. Real failures — anything that is not a 401 — still reject
             * as an AxiosError and still get logged, which is the whole point:
             * an expired session should not be able to bury a real error.
             */
            return Promise.reject(new CancelledError({ silent: true }))
        }

        return Promise.reject(error)
    }
)

const request = {
    get: <T>(
        endpoint: string,
        options?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
        return api.get(endpoint, options)
    },
    post: (
        endpoint: string,
        data?: unknown,
        options?: RawAxiosRequestConfig
    ): Promise<AxiosResponse> => {
        return api.post(endpoint, data, options)
    },
    put: (
        endpoint: string,
        data: unknown,
        options?: AxiosRequestConfig
    ): Promise<AxiosResponse> => {
        return api.put(endpoint, data, options)
    },
    patch: (
        endpoint: string,
        data?: unknown,
        options?: AxiosRequestConfig
    ): Promise<AxiosResponse> => {
        return api.patch(endpoint, Object.assign({}, data), options)
    },
    delete: (
        endpoint: string,
        data?: unknown,
        options?: AxiosRequestHeaders
    ): Promise<AxiosResponse> => {
        return api.delete(endpoint, {
            data: data,
            headers: options,
        })
    },
    options: (
        endpoint: string,
        options?: AxiosRequestConfig
    ): Promise<AxiosResponse> => {
        return api.options(endpoint, options)
    },

    head: (
        endpoint: string,
        options?: AxiosRequestConfig
    ): Promise<AxiosResponse> => {
        return api.head(endpoint, options)
    },
}

export default request
